export type FuncName =
  | 'sin'
  | 'cos'
  | 'tan'
  | 'asin'
  | 'acos'
  | 'atan'
  | 'sinh'
  | 'cosh'
  | 'tanh'
  | 'sqrt'
  | 'cbrt'
  | 'exp'
  | 'log'
  | 'ln'
  | 'log2'
  | 'log10'
  | 'abs'
  | 'sign'
  | 'floor'
  | 'ceil'
  | 'round'
  | 'min'
  | 'max'
  | 'pow';

export type ExprNode =
  | { kind: 'num'; value: number }
  | { kind: 'var'; name: string }
  | { kind: 'add'; children: ExprNode[] }
  | { kind: 'mul'; children: ExprNode[] }
  | { kind: 'pow'; base: ExprNode; exp: ExprNode }
  | { kind: 'func'; name: FuncName; args: ExprNode[] };

export const num = (value: number): ExprNode => ({ kind: 'num', value });

export const variable = (name: string): ExprNode => ({ kind: 'var', name });

export const add = (children: ExprNode[]): ExprNode => ({
  kind: 'add',
  children,
});

export const mul = (children: ExprNode[]): ExprNode => ({
  kind: 'mul',
  children,
});

export const pow = (base: ExprNode, exp: ExprNode): ExprNode => ({
  kind: 'pow',
  base,
  exp,
});

export const func = (name: FuncName, args: ExprNode[]): ExprNode => ({
  kind: 'func',
  name,
  args,
});

export function cloneDeep(n: ExprNode): ExprNode {
  switch (n.kind) {
    case 'num': {
      return { kind: 'num', value: n.value };
    }
    case 'var': {
      return { kind: 'var', name: n.name };
    }
    case 'add': {
      return { kind: 'add', children: n.children.map((c) => cloneDeep(c)) };
    }
    case 'mul': {
      return { kind: 'mul', children: n.children.map((c) => cloneDeep(c)) };
    }
    case 'pow': {
      return {
        kind: 'pow',
        base: cloneDeep(n.base),
        exp: cloneDeep(n.exp),
      };
    }
    case 'func': {
      return {
        kind: 'func',
        name: n.name,
        args: n.args.map((a) => cloneDeep(a)),
      };
    }
  }
}

export function equals(a: ExprNode, b: ExprNode): boolean {
  if (a.kind !== b.kind) return false;

  switch (a.kind) {
    case 'num': {
      const bNum = b as typeof a;
      return a.value === bNum.value;
    }
    case 'var': {
      const bVar = b as typeof a;
      return a.name === bVar.name;
    }
    case 'add': {
      const bAdd = b as typeof a;
      if (a.children.length !== bAdd.children.length) return false;
      return a.children.every((child, i) => equals(child, bAdd.children[i]!));
    }
    case 'mul': {
      const bMul = b as typeof a;
      if (a.children.length !== bMul.children.length) return false;
      return a.children.every((child, i) => equals(child, bMul.children[i]!));
    }
    case 'pow': {
      const bPow = b as typeof a;
      return equals(a.base, bPow.base) && equals(a.exp, bPow.exp);
    }
    case 'func': {
      const bFunc = b as typeof a;
      if (a.name !== bFunc.name) return false;
      if (a.args.length !== bFunc.args.length) return false;
      return a.args.every((arg, i) => equals(arg, bFunc.args[i]!));
    }
  }
}
